import type { CreditEstimate } from '../services/billingService';

/**
 * Every price is shown in Naira only — what the wallet takes, markup included — and never a dollar
 * figure. On the caller's own key the wallet takes nothing (their provider bills them): "₦0 on your
 * key". The provider's dollar cost is internal arithmetic here and is never rendered.
 *
 * `POST /api/v3/billing/estimate` prices work exactly as its hold will be priced (the rate, the lab's
 * markup and the minimum), and ₦0 when the caller's own provider key runs it. Its answer becomes Naira
 * per dollar for that lab and model, so any duration or count of the same work converts without
 * another request.
 */
export interface NairaRate {
  /** Wallet Naira per provider dollar; 0 on the caller's own key. */
  perDollar: number;
  /** The wallet's ₦ per dollar, to show the charge in dollars. */
  usdToNgn: number;
  ownKey: boolean;
  /** False in shadow mode: shown as an estimate, nothing is charged yet. */
  enforced: boolean;
}

/** The rate an estimate implies, or null when it cannot say (unpriced, or no dollar cost to divide by). */
export function nairaRateFromEstimate(estimate: CreditEstimate | null | undefined): NairaRate | null {
  if (!estimate) return null;
  if (estimate.ownKey) return { perDollar: 0, usdToNgn: estimate.usdToNgn, ownKey: true, enforced: estimate.enforced };
  if (!estimate.priced || !(estimate.costUsd > 0) || !(estimate.costNgn > 0) || !(estimate.usdToNgn > 0)) return null;
  return { perDollar: estimate.costNgn / estimate.costUsd, usdToNgn: estimate.usdToNgn, ownKey: false, enforced: estimate.enforced };
}

/** "₦28.35", "₦94.50", "₦700", "₦1,120": kobo below ₦100, whole Naira from there. */
export function formatNaira(naira: number): string {
  const value = Math.max(0, naira);
  if (value >= 100) return `₦${Math.round(value).toLocaleString('en-NG')}`;
  const fixed = value.toFixed(2);
  return `₦${fixed.endsWith('.00') ? fixed.slice(0, -3) : fixed}`;
}

/**
 * The price shown for a provider cost, in Naira only: "₦1,120" (what the wallet takes, markup
 * included), "≈ ₦1,120" in shadow mode, "₦0 on your key". Null without a rate: there is no price
 * until the wallet's rate is known, and no dollar figure ever stands in for it.
 */
export function formatPrice(providerUsd: number, rate: NairaRate | null | undefined): string | null {
  if (!rate) return null;
  if (rate.ownKey) return '₦0 on your key';
  const shown = formatNaira(providerUsd * rate.perDollar);
  return rate.enforced ? shown : `≈ ${shown}`;
}

/**
 * A price quoted before the work runs (by duration or count): "≈ ₦1,120", or "₦0 on your key".
 * Null without a rate.
 */
export function formatEstimate(providerUsd: number, rate: NairaRate | null | undefined): string | null {
  if (!rate) return null;
  if (rate.ownKey) return '₦0 on your key';
  return `≈ ${formatNaira(providerUsd * rate.perDollar)}`;
}
